import { forkJoin, switchMap, tap, map, of, catchError, Observable } from 'rxjs';
import { get, post } from '@/shared/api/http';
import { authService } from '../services/auth.service';
import { mapUser, type BackendUASDevice, type BackendUserProfile } from '@/shared/api/user-mapper';

// --- Backend response shapes ---

interface BackendTokenResponse {
  access: string;
  refresh: string;
}

// --- Registration payload ---

interface RegisterPayload {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  operator_name: string;
  operator_registration_number: string;
  operator_registration_valid_until: string;
}

// --- Public API ---

function fetchUserProfile(): Observable<void> {
  return forkJoin({
    profile: get<BackendUserProfile>('/api/v1/users/me/'),
    devices: get<BackendUASDevice[]>('/api/v1/devices/uas-devices/'),
  }).pipe(
    tap(({ profile, devices }) => authService.setUser(mapUser(profile, devices))),
    map(() => undefined),
  );
}

export function initializeAuth(): Observable<void> {
  const hasAccess = authService.isAuthenticated();
  const hasRefresh = !!authService.getRefreshToken();

  if (!hasAccess && !hasRefresh) {
    authService.setInitialized();
    return of(undefined);
  }

  // If only refresh token exists, obtain a new access token first
  const prep$: Observable<void> = hasAccess
    ? of(undefined)
    : new Observable<void>(subscriber => {
        const refresh = authService.getRefreshToken()!;
        const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';
        fetch(`${BASE_URL}/api/v1/auth/token/refresh/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh }),
        })
          .then(res => res.ok ? res.json() as Promise<{ access: string }> : Promise.reject())
          .then(({ access }) => { authService.setToken(access); subscriber.next(); subscriber.complete(); })
          .catch(() => { subscriber.next(); subscriber.complete(); });
      });

  return prep$.pipe(
    switchMap(() => authService.isAuthenticated() ? fetchUserProfile() : of(undefined)),
    catchError(() => { authService.logout(); return of(undefined); }),
    tap(() => authService.setInitialized()),
  );
}

export function register(payload: RegisterPayload): Observable<void> {
  return post<{ detail: string }>('/api/v1/auth/register/', payload).pipe(
    map(() => undefined),
  );
}

export function login(email: string, password: string): Observable<void> {
  return post<BackendTokenResponse>('/api/v1/auth/token/', { email: email, password }).pipe(
    tap(({ access, refresh }) => {
      authService.setToken(access);
      authService.setRefreshToken(refresh);
    }),
    switchMap(() =>
      forkJoin({
        profile: get<BackendUserProfile>('/api/v1/users/me/'),
        devices: get<BackendUASDevice[]>('/api/v1/devices/uas-devices/'),
      })
    ),
    tap(({ profile, devices }) => authService.setUser(mapUser(profile, devices))),
    map(() => undefined),
  );
}
